/**
 * Deep-clone helper for `SceneObject`.
 *
 * Used by the undo system to capture point-in-time copies of scene objects
 * without aliasing live typed arrays. An optional `WeakMap` cache enables
 * structural sharing: any typed-array buffer that hasn't changed since the
 * previous capture (same source reference) reuses its earlier clone instead
 * of being copied again. This keeps undo memory linear in *changes*, not in
 * *snapshots*, even for high-poly meshes where most edits leave geometry
 * untouched.
 *
 * Worker-safe: no DOM, no store imports, no globals.
 */

import type {
  FaceAttributeTable,
  PolygonTopology,
  SceneObject,
} from "../../modeling/types.ts";
import {
  asLoopNormals,
  cloneRenderTriangleIndices,
} from "../../modeling/types.ts";

/**
 * Cache for structural-sharing clones. Keys are *source* references —
 * `SceneObject`s and typed-array buffers — and values are their clones.
 * Two snapshots that point at the same source ref share the same clone.
 *
 * Backed by a `WeakMap` so entries auto-GC when the source ref is dropped
 * by the live store on the next immutable update.
 */
export type SceneObjectCloneCache = WeakMap<object, object>;

/**
 * Single generic clone helper for any value with a same-type `slice()`.
 *
 * Every JS typed array (`Float32Array`, `Uint32Array`, `Uint8Array`, etc.)
 * satisfies `{ slice(): T }`, so this one function covers them all without
 * the unsafe `as` casts the per-type variants would need. Exported so other
 * snapshot paths (notably `storeUndo.ts`) can share the same cache for
 * non-`SceneObject` typed arrays like `editMode.curveEditVertices`.
 */
export function cachedClone<T extends { slice(): T }>(
  src: T,
  cache?: SceneObjectCloneCache,
): T {
  if (!cache) return src.slice();
  const cached = cache.get(src as unknown as object) as T | undefined;
  if (cached) return cached;
  const fresh = src.slice();
  cache.set(src as unknown as object, fresh);
  return fresh;
}

function cachedCloneCustom<T extends object>(
  src: T,
  clone: (src: T) => T,
  cache?: SceneObjectCloneCache,
): T {
  if (!cache) return clone(src);
  const cached = cache.get(src) as T | undefined;
  if (cached) return cached;
  const fresh = clone(src);
  cache.set(src, fresh);
  return fresh;
}

/**
 * Cache-aware variant of `cloneTopology` in `modeling/topology.ts`. The
 * centralised helper produces a fully-independent copy; this one reuses
 * buffers through the snapshot cache where the source reference is
 * unchanged, which keeps undo memory linear in *changes* rather than
 * *snapshots*.
 *
 * Every field listed on `PolygonTopology` must appear here — any new
 * per-edge attribute added to the central `cloneTopology` must be added
 * here at the same time. The persistence clone tests in
 * `test/persistence/codec.test.ts` and the snapshot round-trip tests in
 * `test/persistence/fieldGaps.test.ts` guard that invariant.
 */
function cloneTopology(
  t: PolygonTopology,
  cache?: SceneObjectCloneCache,
): PolygonTopology {
  return {
    offsets: cachedClone(t.offsets, cache),
    corners: cachedClone(t.corners, cache),
    edges: cachedClone(t.edges, cache),
    cornerEdges: cachedClone(t.cornerEdges, cache),
    // edgeFlags is mutated in place by topology edit ops, so always
    // duplicate the buffer rather than sharing it through the cache.
    edgeFlags: t.edgeFlags.slice(),
    // edgeCreases follows the same in-place mutation pattern as edgeFlags
    // (the crease slider writes into `topology.edgeCreases`), so every
    // snapshot needs its own buffer.
    ...(t.edgeCreases ? { edgeCreases: t.edgeCreases.slice() } : {}),
  };
}

function cloneFaceAttributes(
  attrs: FaceAttributeTable | undefined,
  cache?: SceneObjectCloneCache,
): FaceAttributeTable | undefined {
  if (!attrs) return undefined;
  const clone: FaceAttributeTable = {};
  if (attrs.materialIndex) {
    clone.materialIndex = cachedClone(attrs.materialIndex, cache);
  }
  if (attrs.shading) {
    clone.shading = cachedClone(attrs.shading, cache);
  }
  if (attrs.groups) {
    clone.groups = attrs.groups.map((g) => ({
      id: g.id,
      name: g.name,
      faces: new Set(g.faces),
    }));
  }
  return clone;
}

/**
 * Deep-clone a `SceneObject`. All typed arrays are copied; nested
 * collections (face-texel layers) are
 * fully duplicated. The returned object shares no mutable state with `o`.
 *
 * If `cache` is provided, structural sharing applies at every typed-array
 * level: two snapshots that reference the same source buffer will share
 * the same clone instead of copying twice. The cache also dedupes whole
 * `SceneObject` clones — if `o` was already cloned through this cache,
 * the existing clone is returned.
 */
export function cloneSceneObject(
  o: SceneObject,
  cache?: SceneObjectCloneCache,
): SceneObject {
  if (cache) {
    const cached = cache.get(o) as SceneObject | undefined;
    if (cached) return cached;
  }
  const clone: SceneObject = {
    id: o.id,
    name: o.name,
    objectKind: o.objectKind,
    position: [o.position[0], o.position[1], o.position[2]],
    rotation: [o.rotation[0], o.rotation[1], o.rotation[2]],
    scale: [o.scale[0], o.scale[1], o.scale[2]],
    vertices: cachedClone(o.vertices, cache),
    indices: cachedCloneCustom(o.indices, cloneRenderTriangleIndices, cache),
    // `LoopNormals` is a branded `Float32Array`. `slice()` on the brand returns
    // a plain `Float32Array`, so we round-trip through the unbranded type and
    // re-brand at the boundary — same pattern as `cloneLoopNormals`.
    loopNormals: asLoopNormals(
      cachedClone<Float32Array>(o.loopNormals as Float32Array, cache),
    ),
    topology: cloneTopology(o.topology, cache),
    uvs: o.uvs ? cachedClone(o.uvs, cache) : undefined,
    baseVertices: o.baseVertices
      ? cachedClone(o.baseVertices, cache)
      : undefined,
    skinRadii: o.skinRadii ? cachedClone(o.skinRadii, cache) : undefined,
    vertexGroups: o.vertexGroups
      ? o.vertexGroups.map((g) => ({
          id: g.id,
          name: g.name,
          indices: new Set(g.indices),
        }))
      : undefined,
    materialSlots: o.materialSlots.map((s) => ({ ...s })),
    faceAttributes: cloneFaceAttributes(o.faceAttributes, cache),
    // Generator ref must be deep-cloned so undo snapshots capture the params
    // independently from the live store.  Without this, applyGenerator (which
    // sets generator: undefined) would silently strip the ref from every prior
    // snapshot that shared the same object reference, making undo a no-op.
    generator: o.generator
      ? {
          generatorId: o.generator.generatorId,
          params: structuredClone(o.generator.params),
          worldSpace: o.generator.worldSpace,
          geometrySignature: o.generator.geometrySignature,
        }
      : undefined,
    shading: o.shading,
    autoSmoothAngle: o.autoSmoothAngle,
    light: o.light ? { ...o.light } : undefined,
    mannequinModelId: o.mannequinModelId,
    originOffset: o.originOffset
      ? [o.originOffset[0], o.originOffset[1], o.originOffset[2]]
      : undefined,
    imported: o.imported,
    curveData: o.curveData ? structuredClone(o.curveData) : undefined,
    imagePlane: o.imagePlane ? { ...o.imagePlane } : undefined,
  };
  if (cache) cache.set(o, clone);
  return clone;
}
